import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import History from "@tiptap/extension-history";
import ListItem from "@tiptap/extension-list-item";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import {
    EditorContent,
    type EditorContentProps,
    type JSONContent,
    useEditor,
} from "@tiptap/react";
import { useEffect } from "react";
import { DeleteListOnBackspace } from "./delete-list-on-backspace";
import { SingleParagraph } from "./single-paragraph";

type TiptapBulletPointsProps = Omit<EditorContentProps, "editor"> & {
    onValueChange: (value: string) => void;
    withNestedBulletPoints?: boolean;
};

const bulletPointPrefix = "•";
const bulletPointPrefixWithSpace = `${bulletPointPrefix} `;
const indentSize = 2;

export const tiptapJsonToFormattedText = (json: JSONContent): string => {
    if (!json?.content) return "";

    const rootBulletList = json.content.find(
        (item) => item.type === "bulletList",
    );

    if (!rootBulletList?.content) return "";

    return processBulletList(rootBulletList.content, 0).trim();
};

const processBulletList = (listItems: JSONContent[], depth: number): string => {
    const indent = " ".repeat(depth * indentSize);

    return listItems
        .map((listItem) => {
            if (listItem.type !== "listItem") return "";

            // Extract paragraph text
            const paragraphNode = listItem.content?.find(
                (item) => item.type === "paragraph",
            );

            const textContent =
                paragraphNode?.content
                    ?.filter((item) => item.type === "text")
                    .map((item) => item.text)
                    .join("") ?? "";

            // Check for nested bullet lists
            const nestedList = listItem.content?.find(
                (item) => item.type === "bulletList",
            );

            const currentLine = `${indent}${bulletPointPrefix} ${textContent.trim()}`;

            const nestedLines = nestedList?.content
                ? `\n${processBulletList(nestedList.content, depth + 1)}`
                : "";

            return currentLine + nestedLines;
        })
        .filter(Boolean)
        .join("\n");
};

export const appendBulletPoint = ({
    bulletPoints,
    newBulletPoint,
}: {
    bulletPoints: string;
    newBulletPoint: string;
}) => {
    const lines = bulletPoints.split("\n").filter((line) => line.trim() !== "");

    lines.push(`${bulletPointPrefixWithSpace}${newBulletPoint.trim()}`);

    return lines.join("\n");
};

const escapeHtml = (text: string) =>
    text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

export const bulletPointsToTiptapHtml = (value: string): string => {
    if (!value?.trim()) return "";

    const lines = value.split("\n");

    let html = "";
    let currentDepth = 0;

    for (const rawLine of lines) {
        const line = rawLine.trimEnd(); // keep left spaces for depth detection

        if (!line.trim()) continue;

        // Count leading spaces to detect nesting level
        const leadingSpaces = rawLine.length - rawLine.trimStart().length;
        const depth = Math.floor(leadingSpaces / indentSize);

        // Extract text after bullet
        let text = line.trim();
        if (text.startsWith(bulletPointPrefixWithSpace)) {
            text = text.slice(bulletPointPrefixWithSpace.length);
        } else if (text.startsWith(bulletPointPrefix)) {
            text = text.slice(bulletPointPrefix.length);
        }
        text = text.trim();

        // Adjust <ul> nesting
        if (depth > currentDepth) {
            html += "<ul>".repeat(depth - currentDepth);
        } else if (depth < currentDepth) {
            html += "</ul>".repeat(currentDepth - depth);
        }
        currentDepth = depth;

        html += `<li><p>${escapeHtml(text)}</p></li>`;
    }

    // Close any remaining open lists
    html += "</ul>".repeat(currentDepth);

    // Wrap top-level list if not already wrapped
    if (!html.startsWith("<ul>")) {
        html = `<ul>${html}</ul>`;
    }

    return html;
};

const TiptapBulletPoints = ({
    onValueChange,
    content,
    disabled,
    withNestedBulletPoints = true,
    ...props
}: TiptapBulletPointsProps) => {
    const editor = useEditor({
        extensions: [
            // Extend the root schema to say it can only contain one single bulleted list.
            Document.extend({ content: "bulletList?" }),
            Paragraph,
            Text,
            BulletList,
            ...(withNestedBulletPoints
                ? [ListItem]
                : [
                      ListItem.extend({
                          content: "paragraph",
                      }),
                  ]),
            History,
            DeleteListOnBackspace,
            SingleParagraph,
        ],
        editorProps: {
            attributes: {
                class: "outline-none m-0 flex-1 [&_ol]:list-decimal [&_ul,&_ol]:px-4 [&>ul,&>ol]:my-5 [&_ul,&_ol]:mr-2 [&_ul>li,&_ol>li]:mb-1 [&_ul>li:last-child,&_ol>li:last-child]:mb-0 [&>ul:first-child,&>ol:first-child]:m-0",
            },
            handleKeyDown(_, event) {
                // Avoid focus leaving the editor when indenting with Tab
                if (withNestedBulletPoints && event.key === "Tab") {
                    event.preventDefault();
                    event.stopPropagation();
                }
            },
        },
        onUpdate: ({ editor }) => {
            const json = editor.getJSON();

            const formattedText = tiptapJsonToFormattedText(json);
            onValueChange(formattedText);
        },
        content: bulletPointsToTiptapHtml(content),
        editable: !disabled,
    });

    /**
     * Keep hook form state in sync with editor state
     * If the form value gets updated e.g. by calling setValue on the form controller
     * this code syncs the editor state with the form value
     */
    useEffect(() => {
        if (!editor) {
            return;
        }

        const json = editor.getJSON();
        const formattedText = tiptapJsonToFormattedText(json);

        if (formattedText === content) {
            return;
        }

        editor.commands.setContent(bulletPointsToTiptapHtml(content));
    }, [content, editor]);

    if (!editor) {
        return null;
    }

    return <EditorContent editor={editor} {...props} />;
};

export { TiptapBulletPoints };
